import React, { useEffect, useMemo, useState } from 'react';
import { Badge } from '@components/atoms/Badge';
import { Loading } from '@components/atoms/Loading';
import ErrorMessage from '@components/molecules/ErrorMessage';
import { Button } from '@components/atoms/Button';
import { useTasks } from '@hooks/api/task/useTasks';
import { useTranslation } from '@hooks/useTranslation';
import { getTranslatedTaskStatusLabel } from '@utils/options';
import { pluralize } from '@pages/dashboard/client/clientProgressFormat';
import { formatDateOnly } from '@utils/formatDate';
import { Task } from '@models/task.model';
import {
  CLIENT_TASK_GROUP_ORDER,
  ClientTaskGroupId,
  classifyClientTask,
  daysLateFor,
  sortGroupTasks,
} from './clientTaskGroups';

interface ClientTasksTabProps {
  projectId: string;
  /** `access.scheduleDates`: Start/End columns exist only when ON (never an empty column). */
  showDates: boolean;
  onSelectTask: (taskId: string) => void;
  onCountChange?: (count: number) => void;
}

/**
 * Sprint 44, story-226: regrouped on the subcontractor Work tab's pattern: one table per status
 * group (Awaiting your approval, In progress, Upcoming, Complete), no money, no crew, no write
 * control. Sprint 43, story-224. Read-only task list for a CLIENT whose checklist has `tasks` ON. A flat
 * list of the rows the server returns (it already gates by `tasks` and nulls dates when
 * `scheduleDates` is OFF); no subtask nesting, drag handle, checkbox, menu, add or import.
 * Mounted only while its tab is selected, so it never queries for a tab the client cannot see.
 */
export const ClientTasksTab: React.FC<ClientTasksTabProps> = ({
  projectId,
  showDates,
  onSelectTask,
  onCountChange,
}) => {
  const tProjects = useTranslation('projects');
  const tTaskDetail = useTranslation('taskDetail');
  const tCommon = useTranslation('common');
  const tProgress = useTranslation('clientProgress');
  const { tasks, loading, error, refetch } = useTasks(projectId);
  const [completeOpen, setCompleteOpen] = useState(false);

  const groups = useMemo(() => {
    const buckets: Record<ClientTaskGroupId, Task[]> = {
      awaiting: [],
      inProgress: [],
      upcoming: [],
      complete: [],
    };
    for (const task of tasks) {
      const group = classifyClientTask(task.status);
      if (group) buckets[group].push(task);
    }
    return CLIENT_TASK_GROUP_ORDER.map((id) => ({
      id,
      tasks: sortGroupTasks(id, buckets[id], showDates),
    })).filter((group) => group.tasks.length > 0);
  }, [tasks, showDates]);
  const shownCount = groups.reduce((sum, group) => sum + group.tasks.length, 0);

  useEffect(() => {
    if (!loading && !error) onCountChange?.(shownCount);
  }, [loading, error, shownCount, onCountChange]);

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loading size="xl" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col gap-3">
        <ErrorMessage message={tProjects.clientTabLoadError} />
        <div>
          <Button variant="tertiary" onClick={() => refetch()}>
            {tCommon.retry}
          </Button>
        </div>
      </div>
    );
  }

  if (shownCount === 0) {
    return (
      <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
        {tProjects.clientTasksEmpty}
      </p>
    );
  }

  const open = (task: Task) => {
    if (task.taskId) onSelectTask(task.taskId);
  };

  const groupLabel: Record<ClientTaskGroupId, string> = {
    awaiting: tProgress.groupAwaiting,
    inProgress: tProgress.groupInProgress,
    upcoming: tProgress.groupUpcoming,
    complete: tProgress.groupComplete,
  };

  return (
    <div className="flex flex-col gap-3" data-testid="client-tasks-groups">
      {groups.map((group) => {
        const collapsible = group.id === 'complete';
        const expanded = !collapsible || completeOpen;
        const showTiming = showDates && group.id !== 'complete';
        return (
          <section
            key={group.id}
            data-testid={`client-task-group-${group.id}`}
            // Focusable (not tabbable) so a progress-header counter can scroll to and focus it.
            tabIndex={-1}
            className="overflow-x-auto rounded-md bg-white dark:bg-gray-700 ring-1 ring-gray-900/5 dark:ring-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400">
            {collapsible ? (
              <button
                type="button"
                data-testid="client-task-group-complete-toggle"
                aria-expanded={expanded}
                onClick={() => setCompleteOpen((value) => !value)}
                className="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400">
                <span>
                  {expanded ? '\u25BE ' : '\u25B8 '}
                  {groupLabel[group.id]}
                </span>
                <span className="text-gray-500 dark:text-gray-300">
                  {group.tasks.length}
                </span>
              </button>
            ) : (
              <div className="flex items-center justify-between px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white">
                <span>{groupLabel[group.id]}</span>
                <span
                  data-testid={`client-task-group-${group.id}-count`}
                  className="text-gray-500 dark:text-gray-300">
                  {group.tasks.length}
                </span>
              </div>
            )}
            {expanded && (
              <table className="w-full text-sm text-left">
                <thead className="text-xs uppercase text-gray-500 dark:text-gray-400">
                  <tr>
                    <th scope="col" className="px-4 py-3">
                      {tProjects.clientTasksColTask}
                    </th>
                    <th scope="col" className="px-4 py-3">
                      {tProjects.clientTasksColStatus}
                    </th>
                    <th scope="col" className="px-4 py-3 max-[1023px]:hidden">
                      {tProjects.clientTasksColType}
                    </th>
                    {showDates && (
                      <>
                        <th scope="col" className="px-4 py-3">
                          {tProjects.clientTasksColStart}
                        </th>
                        <th scope="col" className="px-4 py-3">
                          {tProjects.clientTasksColEnd}
                        </th>
                      </>
                    )}
                    {showTiming && (
                      <th scope="col" className="px-4 py-3">
                        {tProgress.colTiming}
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {group.tasks.map((task) => {
                    const late = showTiming ? daysLateFor(task) : null;
                    return (
                      <tr
                        key={task.taskId}
                        role="button"
                        tabIndex={0}
                        onClick={() => open(task)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            open(task);
                          }
                        }}
                        className="cursor-pointer border-t border-gray-100 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400">
                        <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">
                          {task.name}
                        </td>
                        <td className="px-4 py-3">
                          {task.status && (
                            <Badge variant="secondary" size="sm">
                              {getTranslatedTaskStatusLabel(
                                // Internal workflow never reaches a client: it reads as Active.
                                task.status === 'INTERNAL_APPROVAL'
                                  ? 'ACTIVE'
                                  : task.status,
                                tTaskDetail,
                              )}
                            </Badge>
                          )}
                        </td>
                        <td className="px-4 py-3 max-[1023px]:hidden">
                          {task.taskType}
                        </td>
                        {showDates && (
                          <>
                            <td className="px-4 py-3">
                              {formatDateOnly(task.startTime)}
                            </td>
                            <td className="px-4 py-3">
                              {formatDateOnly(task.endTime)}
                            </td>
                          </>
                        )}
                        {showTiming && (
                          <td
                            data-testid="client-task-timing"
                            className="px-4 py-3 text-red-700 dark:text-red-300">
                            {late !== null
                              ? pluralize(tProgress, 'daysLate', late)
                              : ''}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </section>
        );
      })}
    </div>
  );
};

export default ClientTasksTab;
